<script setup lang="ts">
import { computed, ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { custom_moment } from '@/plugins/moment-cultured';

import { useGeneralPurposeModalStore, useSessionStore, useSurveyStore, useDashboardStore } from 'src/store';
import { getSurveyDefinition, updateSurveyProgress } from 'src/api/endpoints/calendarWeb';
import { handleApiRequest } from 'src/api/handleApiRequest';
import { toSurveyDefinitionModel, toSurveyProgressModel } from 'src/services/surveys/surveyService';
import { loadPatientConfig } from 'src/pages/TheDataHydrator';
import type { SurveyProgressDenormalizedQuestionAndAnswerModel } from 'src/types/model';
import { emptySurveyDefinition } from 'src/services/surveys/utils';
import Box from 'src/components/BoxContainer.vue';
import MultiViewLayout from 'src/components/layouts/MultiViewLayout.vue';
import ProgressBar from 'src/components/ProgressBar.vue';
import TheSurveyPage from './components/TheSurveyPage.vue';
import TheSurveySubmit from './components/TheSurveySubmit.vue';
import type { SurveyAnswerRequest, Task } from 'src/types/webContracts';
import {
  SurveyComponentTypeEnum,
  SurveyComponentQuestionTypeEnum,
  SurveyTypeEnum,
} from 'src/constants/survey';
import { GeneralPurposeVariant, TaskType, EventCompletionStatus } from 'src/constants/enums';
import { getPatientStatisticSummaries } from 'src/api/endpoints/statisticsWeb';
import { getPatientProfileData, getTasks } from 'src/api/endpoints/userWeb';
import type { ITheSurveyPageComponent } from 'src/types/components';
import { useQuasar } from 'quasar';
import {useI18n} from 'vue-i18n';
import TheSurveyReview from "@/pages/Survey/components/TheSurveyReview.vue";

const {t}= useI18n();
const sessionStore = useSessionStore();
const surveyModule = useSurveyStore();
const generalPurposeModalStore = useGeneralPurposeModalStore()
const dashboardStore = useDashboardStore()
const router = useRouter();
const $q = useQuasar()
const isMagicLinkUser = computed(() => sessionStore.isMagicLinkUser);

const page = ref({} as { value: ITheSurveyPageComponent | null });
const isLoading = ref(true);
const declineToComplete = ref(false);

const props = defineProps({
  surveyId: {
    type: Number,
    required: true,
  },
  tickSignature: {
    type: String,
    required: true,
  },
  pageNumber: {
    type: String,
    required: true,
  },
  redirectRoute: {
    type: String,
    required: false,
    default: 'Dashboard.Home',
  },
});

const redirectRoute = ref(props.redirectRoute);

onMounted(async () => {
  const languageId = sessionStore.user?.preferredLanguageId || 0;
  if (isMagicLinkUser.value) {
    redirectRoute.value = 'Dashboard.MLHome';
  }

  let { eventDate } =
    dashboardStore.tasks?.find((item) => item.tickSignature === props.tickSignature) || ({} as Task);

  let tickSignature = props.tickSignature;

  //fetch the questions and possible answers. Load patientConfig in parallel (no added latency — the survey
  //definition fetch is the long pole) so the survey adapter can read hasConsentedToDataAnonymization reliably
  //even when the survey URL is loaded directly/refreshed (the dashboard hydrator may not have run yet).
  const [surveyDefinitionResponse] = await Promise.all([
    handleApiRequest(() =>
      getSurveyDefinition(props.surveyId, languageId, eventDate, tickSignature), null, $q
    ),
    dashboardStore.patientConfig ? Promise.resolve() : loadPatientConfig(dashboardStore, $q),
    // If the survey URL is loaded directly or refreshed, the dashboard tasks may not be loaded
    // if the eventDate is not available, fetch the tasks to determine the eventDate.
    !eventDate
    ? handleApiRequest(() => getTasks(EventCompletionStatus.All), null, $q)
        .then(res => {
          const task = res?.result?.tasks?.find(
            (item) => item.tickSignature === props.tickSignature
          );
          if (task) eventDate = task.eventDate;
        })
    : Promise.resolve(),
  ]);

  if (surveyDefinitionResponse?.result) {
    const surveyDefinitionModel = toSurveyDefinitionModel(surveyDefinitionResponse.result);
    surveyModule.setSurveyDefinition(surveyDefinitionModel);

    
    const painRatingSurveys = [
      SurveyTypeEnum.Pain_and_Opioid_Pre_Op,
      SurveyTypeEnum.Pain_and_Opioid_Post_Op,
      SurveyTypeEnum.Pain_and_Opioid_Non_surgical,
    ];

    //reason: pain rating surveys dont appear in the todo list and the event date is stored separately for them.
    //        Must wait until the survey definition is loaded to check the survey type
    if (painRatingSurveys.includes(surveyModule.surveyDefinition?.surveyType as SurveyTypeEnum)) {
      eventDate =
        (dashboardStore.patient?.painAndOpioidData.todoDateTime as string) || custom_moment().utc().format();
      tickSignature = (dashboardStore.patient?.painAndOpioidData.todoTickSignature as string) || '';
    }

    const surveyProgressModel = toSurveyProgressModel(
      props.surveyId,
      eventDate,
      tickSignature,
      surveyDefinitionModel,
      surveyDefinitionResponse.result
    );
    surveyModule.setSurveyProgress(surveyProgressModel);

    //Initialize a survey response
    if (surveyDefinitionResponse?.result) {
      surveyModule.setSyncPageVisibilityAndQuestionText(surveyDefinitionResponse.result.survey.pageList);
      if (props.pageNumber) {
        const page = surveyModule.surveyDefinition?.pages.find(
          (p) => p.renderingConfig.order === parseInt(props.pageNumber, 10)
        );
        surveyModule.setCurrentPage(page);
      } else {
        surveyModule.setCurrentPageToNextVisiblePage({});
      }
    }
  }

  document.querySelectorAll('html')[0].scrollTop = 0;

  isLoading.value = false;
});

const isProcessing = ref(false)
const survey = computed(() => surveyModule.surveyDefinition || emptySurveyDefinition());
const isEngagement = computed(() => survey.value.isEngagementSurvey);
const currentPage = computed(() => surveyModule.currentPage);
const isComplete = computed(() => surveyModule.surveyProgress.isComplete);
const progressPercentage = computed(() => surveyModule.surveyProgress.progressPercentage);
const engagementNextButtonTextKey = computed(() => isLastPage.value ? 'got_it_button' : 'activate_continue_text');
const isLastPage = computed(() => surveyModule.currentPage === surveyModule.surveyDefinition?.pages.filter((p) => !p.renderingConfig.hidePage).slice(-1).pop());
const hasEnableReview = computed(() => surveyModule.surveyDefinition?.hasEnableReview ?? false); 
const allAnswersAnswered = computed(() => surveyModule.surveyProgress.allAnswersAnswered);
const reviewableAnswers = computed(() => surveyModule.surveyProgress.actualAnswers.filter(ans => !ans.question.renderingConfig.hideFromReview));

const headingText = computed(() => {
  if (!isEngagement.value) {
    return survey.value.name
  } else {
    switch (survey.value.surveyType) {
      case SurveyTypeEnum.Predicted_Progress_Comparisons_Engagement:
        return t('my_progress_title').toString()
      case SurveyTypeEnum.Predicted_Progress_Predictions_Engagement:
        return t('prediction_screen_title').toString()
      case SurveyTypeEnum.Predicted_Progress_Day_42_Engagement:
      case SurveyTypeEnum.Predicted_Progress_Day_42_Engagement_No_Predictions:
        return t('compare_my_progress').toString()
    }
  }
});

const isFirstPage = computed(() => {
  if (!surveyModule.currentPage.pageId) {
    return false;
  }
  const pageOrder = surveyModule.currentPage.renderingConfig.order || 0;
  return pageOrder === 10;
});

function onLayoutBackClick() {
  router.go(-1);
}

async function onBackClick() {
  surveyModule.setCurrentPageToPreviousPage();
  window.scrollTo(0, 0);
  (document?.activeElement as HTMLElement | null)?.blur(); // Prevent back button from retaining focus
}

async function onNextClick() {
  const surveyPage = page.value as ITheSurveyPageComponent;

  const isValid = await surveyPage.validate();
  if (isValid) {
    const languageId = sessionStore.user?.preferredLanguageId || 0;
    const responses = surveyPage
      .getResponses()
      .filter(
        (r: any) =>
          r.permittedAnswer.renderingConfig.isRecorded &&
          typeof r.value !== 'undefined' &&
          r.value !== null
      );
    // If there is an unanswered unskippable question, display error message
    if (
      currentPage.value.questions.some(
        (q) =>
          !q.renderingConfig.canSkip &&
          !responses.some((r: any) => r.question.questionId == q.questionId) &&
          !(q.renderingConfig.notRelevantCheckbox?.isVisible && q.renderingConfig.notRelevantCheckbox?.isChecked)
      )
    ) {
      const title = t('survey_answer_required').toString();
      const template = t('survey_enter_answer').toString();
      generalPurposeModalStore.setModal({
        title,
        template,
        variant: GeneralPurposeVariant.Decision,
      });
      return;
    }
    window.scrollTo(0, 0);
    isProcessing.value = true
    $q.loading.show()

    const page = surveyModule.currentPage;
    const surveyAnswers: SurveyAnswerRequest[] = [];

    const hoistedAnswers = responses.filter(
      (p: any) => p.permittedAnswer.renderingConfig.hoistAnswerToParentFreeResponseField
    );

    responses
      .filter((p: any) => !p.permittedAnswer.renderingConfig.hoistAnswerToParentFreeResponseField)
      .forEach(({ question, permittedAnswer, value }: any) => {
        const progressAnswer: SurveyProgressDenormalizedQuestionAndAnswerModel = {
          page,
          question,
          answerId: permittedAnswer.answerId,
          skipped: false,
        };

        if (
          permittedAnswer.renderingConfig.type === SurveyComponentTypeEnum.textbox ||
          permittedAnswer.renderingConfig.type === SurveyComponentTypeEnum.dropdown
        ) {
          progressAnswer.answerFreeformText = (value as string).toString();
        } else if (permittedAnswer.renderingConfig.type === SurveyComponentTypeEnum.number) {
          progressAnswer.answerId = parseInt(value as string, 10);
        } else if (question.renderingConfig.type === SurveyComponentQuestionTypeEnum.slider) {
            progressAnswer.answerId = parseInt(value as string);
            if (question.renderingConfig.notRelevantCheckbox?.isVisible) {
                progressAnswer.checkboxChecked = question.renderingConfig.notRelevantCheckbox.isChecked;
            }
        }

        const hoistedAnswer = hoistedAnswers.find(
          (p: any) => p.permittedAnswer.parentAnswerId === permittedAnswer.answerId
        );
        if (hoistedAnswer) {
          progressAnswer.answerFreeformText = hoistedAnswer.value as string;
        }

        surveyModule.setAddSurveyProgressAnswer(progressAnswer);

        surveyAnswers.push({
          questionId: question.questionId,
          questionTypeId: question.questionTypeId,
          answerId: progressAnswer.answerId,
          answerFreeformText: progressAnswer.answerFreeformText,
          skipped: progressAnswer.skipped,
          notRelevant: progressAnswer.checkboxChecked,
        });
      });

    //question was skipped
    if (!responses.length) {
      surveyAnswers.push({
        questionId: page.questions[0].questionId,
        questionTypeId: page.questions[0].questionTypeId,
        skipped: true,
        notRelevant: false,
      });

      surveyModule.setAddSurveyProgressAnswer({
        page,
        question: page.questions[0],
        answerId: 0,
        skipped: true,
        checkboxChecked: false,
      });
    }

    const updateProgressResponse = await handleApiRequest(() =>
      updateSurveyProgress({
        eventDate: surveyModule.surveyProgress.eventDate,
        surveyId: surveyModule.surveyProgress.surveyId,
        tickSignature: surveyModule.surveyProgress.tickSignature,
        surveyResponseId: surveyModule.surveyProgress.surveyResponseId,
        languageId,
        surveyAnswers,
      }), null, $q
    );

    if (updateProgressResponse?.result) {
      surveyModule.setSyncPageVisibilityAndQuestionText(updateProgressResponse.result.surveyData.questionnaire.pageList);

      surveyModule.setAllAnswersAnswered(updateProgressResponse.result.allAnswersAnswered);
      
      const isComplete = isLastPage.value || updateProgressResponse?.result.surveyMarkedComplete;

      surveyModule.setIsComplete(isComplete);

      if (!declineToComplete.value) {
        surveyModule.setCurrentPageToNextVisiblePage({});
      }

      // If this is an interrupt survey, and the survey is complete, submit the survey and redirect
      if ((isEngagement.value) && isComplete) {
        onSubmitClick();
        dashboardStore.patient.interruptData.interrupts = dashboardStore.patient.interruptData.interrupts.filter((i) => i.surveyId !== props.surveyId);
        router.replace({ name: redirectRoute.value });
      }
    }
    isProcessing.value = false
    $q.loading.hide()
  }
}

async function onSubmitClick() {
  const languageId = sessionStore.user?.preferredLanguageId || 0;

  isProcessing.value = false
  $q.loading.show()
  
  let tickSignature = surveyModule.surveyProgress.tickSignature;
  if (surveyModule.surveyDefinition?.surveyType === SurveyTypeEnum.PMH) {
    tickSignature = (surveyModule.surveyDefinition.surveyId * -1).toString();
  }

  const updateProgressResponse = await handleApiRequest(() =>
    updateSurveyProgress({
      eventDate: surveyModule.surveyProgress.eventDate,
      surveyId: surveyModule.surveyProgress.surveyId,
      tickSignature: tickSignature,
      surveyResponseId: surveyModule.surveyProgress.surveyResponseId,
      languageId,
      surveyAnswers: [],
      isComplete: true,
    }), null, $q
  );

  // Update the task alread loaded into the store as completed
  dashboardStore.markTaskCompleted({ id: props.surveyId, taskType: TaskType.survey });

  if (updateProgressResponse?.result.surveyMarkedComplete && !isEngagement.value) {
    if (!isMagicLinkUser.value) {
      const patientDataPromise = handleApiRequest(() => getPatientProfileData(false), null, $q);
      const statsPromise = handleApiRequest(() => getPatientStatisticSummaries(), null, $q);

      //behavior: reload the dashboardStore.patient object from the api if a rating is completed
      //reason: the P&O rating rules are calculated by the api in this endpoint and inform how to enforce subsequent pain rating prompts and self-reports
      const refreshPatientData =
        surveyModule.surveyDefinition?.surveyType === SurveyTypeEnum.Pain_and_Opioid_Pre_Op ||
        surveyModule.surveyDefinition?.surveyType === SurveyTypeEnum.Pain_and_Opioid_Non_surgical ||
        surveyModule.surveyDefinition?.surveyType === SurveyTypeEnum.Pain_and_Opioid_Post_Op;

      //reason: Promise.all() does not like attempts to build multi-type arrays beforehand. So conditionally include the ones needed
      const [statsResponse, patientResponse] = await Promise.all([
        statsPromise,
        refreshPatientData ? patientDataPromise : undefined,
      ]);

      if (statsResponse) {
        dashboardStore.loadPatientWearables(statsResponse.result);
      }

      if (patientResponse) {
        dashboardStore.loadPatient(patientResponse.result);
      }
    }

    // Show success notification for pages using the "TheSurveyReview".
    if (hasEnableReview.value) $q.notify({type: 'message', message: t('checkin_complete_title')});
    
    router.replace({ name: redirectRoute.value });
  }

  isProcessing.value = false
  $q.loading.hide()
}

async function onDeclineClick() {
  declineToComplete.value = true;
  onNextClick();
  // Remove the task
  dashboardStore.removeTask({ id: props.surveyId, taskType: TaskType.survey });
  router.replace({ name: redirectRoute.value });
}

function reviewEditClick(toPgeId: number) {
  window.scrollTo(0, 0);
  
  isProcessing.value = true;
  
  $q.loading.show();

  surveyModule.jumpToPage(toPgeId);
  
  $q.loading.hide();
  
  isProcessing.value = false;
}
</script>

<template>
  <q-page class="survey" padding>
      <MultiViewLayout :loading="isLoading" :onBackClick="onLayoutBackClick" showFooter >

          <!-- Section Title -->
          <template #title v-if="!isEngagement">{{ t('gen_survey') }}</template>

          <!-- Heading Text -->
          <template #heading> {{ headingText }} </template>

          <template #default>
              <!-- Survey Questions/Content -->
              <div v-show="!isLoading && !isComplete" class="col">
                  <TheSurveyPage @on-decline-click="onDeclineClick" @on-next-click="onNextClick" ref="page"/>
                  <!-- Engagement Next button -->
                  <div v-if="isEngagement" class="row justify-center">
                      <zbm-btn @click="onNextClick" :color="$zb.enums.ButtonStdColorEnum.accent"
                               :height="$zb.enums.ButtonHeightEnum.lg" :width="$zb.enums.ButtonWidthEnum.wFull"
                               :label="t(engagementNextButtonTextKey)" data-testid="nextButton"/>
                  </div>
              </div>
            
            <!-- Submission Views -->
            <template v-if="!isLoading && !isEngagement && isComplete">
              
              <!-- Review Submit -->
              <TheSurveyReview v-if="hasEnableReview && allAnswersAnswered && reviewableAnswers.length"
                               @edit-clicked="reviewEditClick"
                               :revable-anss="reviewableAnswers" />
            
              <!-- Default Submit -->
              <TheSurveySubmit v-else />
            </template>
          </template>

          <template #footer>
              <Box v-if="!isEngagement" v-show="!isLoading" :box-drop-shadow="$zb.enums.BoxDropShadowEnum.lg"
                   :box-padding="$zb.enums.BoxPaddingEnum.p20" :box-width="$zb.enums.BoxWidthEnum.lg" class="col-auto">
                  <ProgressBar @on-back-click="onBackClick" @on-next-click="onNextClick"
                               :backEnabled="!isProcessing && !isFirstPage" :nextEnabled="!isProcessing" :progress="progressPercentage">
                      <template #progress-next-section v-if="isComplete">
                          <div v-if="!hasEnableReview" class="col" />

                          <!-- Submit button -->
                          <div :class="[hasEnableReview ? 'col' : 'col-auto']">
                              <zbm-btn @click="onSubmitClick"
                                       :color="$zb.enums.ButtonStdColorEnum.accent"
                                       data-testid="submitButton"
                                       :height="$zb.enums.ButtonHeightEnum.lg"
                                       :label="t('gen_submit')"
                                       :width="hasEnableReview ? $zb.enums.ButtonWidthEnum.wFull : undefined" />
                          </div>
                      </template>
                  </ProgressBar>
              </Box>
          </template>
      </MultiViewLayout>
  </q-page>
</template>
